import React, { Component } from 'react';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import TextField from 'mz-ui-kit/form/TextField';
import Row from 'mz-ui-kit/core/Row';
import { connectSubpages } from 'app/hocs/subpageManagable';
import {
  Field,
  formValueSelector,
  change as changeFieldValue,
} from 'redux-form';
import { FormattedMessage } from 'translations';
import { Layout, Flex } from 'app/components/base/Layout';
import TransitionInput from 'app/components/base/TransitionInput';
import TranslatedField from 'app/components/common/TranslatedField';
import CountryField from 'mz-ui-kit/form/CountryField';
import CompactInput from 'app/components/base/CompactInput';
import RoundedHeader from 'app/components/base/RoundedHeader';
import UserData from './commons/UserData';
import SpecialInstructions from './commons/SpecialInstructions';
import PasswordBox from './commons/PasswordBox';
import BookSomeoneElseBox from './commons/BookSomeoneElseBox';
import TravelerData from './commons/TravelerData';
import MeetAndGreetBox from './commons/MeetAndGreetBox';
import PhoneMandatoryBox from './commons/PhoneMandatoryBox';
import SignInBox from './commons/SignInBox';
import ProfileSelector from './commons/ProfileSelector';
import SmsNotificationsAmenity from './commons/SmsNotificationsAmenity';
import * as userSessionActions from 'app/actions/user-session';
import * as bookTripActions from 'app/actions/book-trip';
import * as meetAndGreetActions from 'app/actions/meet-and-greet';
import {
  getTripUpgrades,
  isTravelAgentCredits,
  getPhoneCodePrefix,
} from 'app/sagas/selectors';
import Collapsible from 'app/components/base/Collapsible';
import { hook } from 'app/utils/hook';
import { normalizePhone } from 'app/utils/types';
import config from 'config';
import { pushData } from 'app/utils/analytics';

import {
  TA_IATA_FIELD_NAME,
  TA_AGENT_ID_FIELD_NAME,
  LOYALTY_MEMBER_NUMBER_FIELD_NAME,
  EXTRA_CONTACT_FIELDS_HOOK,
  BOOK_TRIP_FORM,
  PHONE_CODE_FIELD_NAME,
  PHONE_NUMBER_FIELD_NAME,
  NO_PHONE_SERVICE_UPON_LANDING_FIELD_NAME,
  DEPARTING_TRIP_FIELD_NAME,
  RETURN_TRIP_FIELD_NAME,
  TRAVELER_FIELD_NAME,
  TA_CONFIRMATION_EMAIL_FIELD_NAME,
  MEET_AND_GREET_BOX_CLOSED,
  UPGRADE_IDS,
  USER_TYPE_TA,
  ON_DEMAND_CATEGORY,
  PRICE_FIELD_NAME,
} from 'app/constants';
import { preprocessTripObject } from 'app/utils/trip';

import style from './style.css';

export const messages = {
  loyalty: { id: 'BOOK_TRIP.LOYALTY_MEMBER_NUMBER' },
  iataCode: { id: 'TA_ACCOUNT.AGENT_PROFILE.IATA_CODE' },
  agentId: { id: 'TA_ACCOUNT.AGENT_PROFILE.AGENT_ID' },
  title: { id: 'BOOK_TRIP.CONTACT_INFO.TITLE' },
  phoneCode: { id: 'PHONE_CODE_PICKER.COUNTRY_CODE' },
  phoneNumber: { id: 'BOOK_TRIP.CONTACT_INFO.PHONE_NUMBER' },
  travelAgentConfirmation: {
    id: 'BOOK_TRIP.CONTACT_INFO.TA_EMAIL_FOR_CONFIRMATION',
  },
  profile: { id: 'BOOK_TRIP.CONTACT_INFO.TRAVELER_PROFILE' },
  noPhoneServiceUponLanding: {
    id: 'BOOK_TRIP.CONTACT_INFO.NO_PHONE_SERVICE_UPON_LANDING',
  },
};

const fieldSize = {
  '> mobile': 50,
  mobile: 'noshrink',
};

const fieldDirection = {
  '> mobile': 'row',
  mobile: 'column',
};

export const selector = formValueSelector(BOOK_TRIP_FORM);

export const mapStateToProps = (state) => {
  const {
    userSession: { user },
    bookTrip: { emailTaken },
  } = state;
  const price = selector(state, PRICE_FIELD_NAME);
  const noPhone = selector(state, NO_PHONE_SERVICE_UPON_LANDING_FIELD_NAME);
  const travelerOpened = selector(state, TRAVELER_FIELD_NAME);
  const upgrades = getTripUpgrades(state);
  const isTACredits = isTravelAgentCredits(state);
  const meetAndGreetChosen = upgrades[UPGRADE_IDS.MEET_AND_GREET];
  const meetAndGreetBoxClosed = selector(state, MEET_AND_GREET_BOX_CLOSED);
  const phonePrefix = getPhoneCodePrefix(
    state,
    BOOK_TRIP_FORM,
    PHONE_CODE_FIELD_NAME
  );

  const trip = selector(state, DEPARTING_TRIP_FIELD_NAME);
  const returnTrip = selector(state, RETURN_TRIP_FIELD_NAME);
  let isFromAirport = false;
  let detailsValues = {};
  let passengerValues = {
    passengersCount: 1,
    extraPaxRequired: false,
  };

  if (trip) {
    const preparedTrip = preprocessTripObject(trip);
    const preparedReturnTrip = returnTrip && preprocessTripObject(returnTrip);
    const {
      meetAndGreetAvailable,
      meetAndGreetDefault,
      passengersCount,
      extraPaxRequired,
      specialInstructionsPlaceholder,
    } = preparedTrip.details;
    detailsValues = {
      meetAndGreetAvailable,
      meetAndGreetDefault,
      specialInstructionsPlaceholder,
    };
    passengerValues = { passengersCount, extraPaxRequired };
    isFromAirport = !!(
      preparedTrip?.routing.fromIataCode ||
      preparedReturnTrip?.routing.fromIataCode
    );
  }

  return {
    onDemand: trip && trip.type === ON_DEMAND_CATEGORY,
    noPhone,
    phonePrefix,
    loggedIn: !!user,
    isTravelAgent: user && user.user_type === USER_TYPE_TA,
    isTACredits,
    isFromAirport,
    user,
    upgrades,
    emailTaken,
    travelerOpened,
    ...detailsValues,
    meetAndGreetChosen,
    meetAndGreetBoxClosed,
    ...passengerValues,
    price: {
      finalChargeCurrency: price.finalChargeCurrency,
      finalChargePrice: price.finalChargePrice,
    },
  };
};

const mapDispatchToProps = {
  ...userSessionActions,
  changeFieldValue,
  toggleOptionalAmenity: bookTripActions.toggleOptionalAmenity,
  getSuggestedMeetAndGreetTrips:
    meetAndGreetActions.getSuggestedMeetAndGreetTrips,
  updateMeetAndGreetTrip: meetAndGreetActions.updateMeetAndGreetTrip,
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const upgrades = {};
  for (const upgrade of stateProps.upgrades) {
    upgrades[upgrade.id] = upgrade.included;
  }
  return {
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    upgrades,
  };
};

const normalizeLoyaltyNumber = (value) => {
  if (!value) return value;
  if (
    config.LOYALTY_PROGRAM_NUMBER_MAX_LEN > 0 &&
    value.length > config.LOYALTY_PROGRAM_NUMBER_MAX_LEN
  ) {
    return value.substr(0, config.LOYALTY_PROGRAM_NUMBER_MAX_LEN);
  }
  if (config.LOYALTY_PROGRAM_NUMBER_ONLY_DIGITS) {
    return value.replace(/[^\d]/g, '');
  }
  return value;
};

/**
 * Contact information box component for desktop, tablet and mobile
 */
const builder = ({ MeetAndGreetSuggestionsComponent }) => {
  class ContactInformation extends Component {
    handleOnChangeNoPhone = (newValue) => {
      const { upgrades, toggleOptionalAmenity, meetAndGreetDefault } =
        this.props;

      // remove Meet&Greet option after user unselect "no phone" checkbox
      if (
        !meetAndGreetDefault &&
        !newValue &&
        upgrades[UPGRADE_IDS.MEET_AND_GREET]
      ) {
        toggleOptionalAmenity(UPGRADE_IDS.MEET_AND_GREET);
      }

      return newValue;
    };

    handleBookSomeoneClick = () => {
      this.props.changeFieldValue(BOOK_TRIP_FORM, TRAVELER_FIELD_NAME, true);
    };

    handleTravelerDataClose = () => {
      this.props.changeFieldValue(BOOK_TRIP_FORM, TRAVELER_FIELD_NAME, false);
    };

    handleAddMeetAndGreet = () => {
      this.props.toggleOptionalAmenity(UPGRADE_IDS.MEET_AND_GREET);
    };

    handleCloseMeetAndGreetBox = () => {
      this.props.changeFieldValue(
        BOOK_TRIP_FORM,
        MEET_AND_GREET_BOX_CLOSED,
        true
      );
    };

    handleSignIn = () => {
      const { location, history, saveRedirectBackUrl } = this.props;
      saveRedirectBackUrl(location);
      history.push('/user/login');
    };

    handleShowSuggestedTripsDialog = () => {
      const {
        subpages,
        meetAndGreetAvailable,
        getSuggestedMeetAndGreetTrips,
        updateMeetAndGreetTrip,
      } = this.props;
      if (meetAndGreetAvailable) return;

      // show screen with results
      subpages.pushPage(MeetAndGreetSuggestionsComponent, {
        onInit: getSuggestedMeetAndGreetTrips,
        onConfirm: (tripId) => {
          updateMeetAndGreetTrip(tripId);
          subpages.popPage();
        },
        onClose: () => subpages.popPage(),
      });
    };

    handleFormFillStart = () => {
      const { price } = this.props;
      pushData('begin-checkout-started-filling-personal-info', {
        currency: price.finalChargeCurrency,
        totalPrice: price.finalChargePrice,
      });
    };

    render() {
      const {
        mobile,
        upgrades,
        noPhone,
        loggedIn,
        emailTaken,
        isFromAirport,
        isTravelAgent,
        isTACredits,
        travelerOpened,
        phonePrefix,
        meetAndGreetAvailable,
        meetAndGreetDefault,
        meetAndGreetChosen,
        meetAndGreetBoxClosed,
        passengersCount,
        extraPaxRequired,
        specialInstructionsPlaceholder,
        formName,
        onDemand,
      } = this.props;

      const modifiers = {
        [style.mobile]: mobile,
      };

      const showBookingForSomeoneElse = travelerOpened || extraPaxRequired;
      const titleMessage =
        mobile || !isTravelAgent ? messages.title : messages.profile;
      return (
        <Layout
          className={classnames(style.container, modifiers)}
          direction="column"
          nowrap
        >
          <div className={style.title}>
            <div>
              <RoundedHeader icon={{ family: 'mozio', name: 'contact-info' }}>
                <FormattedMessage {...titleMessage} />
              </RoundedHeader>
            </div>
          </div>
          {!loggedIn && config.SHOW_SIGNIN_BUTTON && (
            <Flex size="noshrink">
              <SignInBox onClickSignIn={this.handleSignIn} />
            </Flex>
          )}
          {!config.TUI_CS_ADJUSTMENTS && (
            <Flex size="noshrink" className={style.field}>
              <ProfileSelector />
            </Flex>
          )}
          <UserData onFormFillStart={this.handleFormFillStart} />
          <Layout size="noshrink" direction={fieldDirection} nowrap>
            <Flex className={style.phoneCode}>
              <TranslatedField
                appearance="btp-field"
                component={CountryField}
                name={PHONE_CODE_FIELD_NAME}
                label={<FormattedMessage {...messages.phoneCode} />}
                placeholder={<FormattedMessage {...messages.phoneCode} />}
              />
            </Flex>
            <Flex className={style.phoneNumber}>
              <TranslatedField
                appearance="btp-field"
                name={PHONE_NUMBER_FIELD_NAME}
                component={TextField}
                renderPrefix={() => phonePrefix}
                label={<FormattedMessage {...messages.phoneNumber} />}
                placeholder={
                  !phonePrefix ? (
                    <FormattedMessage {...messages.phoneNumber} />
                  ) : (
                    ''
                  )
                }
                normalize={normalizePhone}
              />
            </Flex>
          </Layout>
          <Layout size="noshrink" direction={fieldDirection} nowrap>
            {!meetAndGreetChosen &&
              config.PHONE_SERVICE_SHOW &&
              isFromAirport && (
                <Field
                  compact
                  size={fieldSize}
                  className={style.noPhoneServiceCheckbox}
                  component={CompactInput}
                  name={NO_PHONE_SERVICE_UPON_LANDING_FIELD_NAME}
                  normalize={this.handleOnChangeNoPhone}
                  nolabel
                  type="checkbox"
                  placeholder={
                    <FormattedMessage {...messages.noPhoneServiceUponLanding} />
                  }
                  reversedOrder
                />
              )}
            <Flex size={fieldSize} className={style.field}>
              {!onDemand && (
                <Collapsible
                  className={style.bookSomeoneElseOpened}
                  endClassName={style.bookSomeoneElseClosed}
                  contentClassName={style.bookSomeoneElseContent}
                  opened={
                    !(
                      travelerOpened ||
                      extraPaxRequired ||
                      passengersCount === 1
                    )
                  }
                >
                  <BookSomeoneElseBox
                    onClickAdd={this.handleBookSomeoneClick}
                  />
                </Collapsible>
              )}
            </Flex>
          </Layout>
          {onDemand && <PhoneMandatoryBox />}
          {!meetAndGreetBoxClosed &&
            !meetAndGreetDefault &&
            !meetAndGreetChosen &&
            noPhone && (
              <MeetAndGreetBox
                meetAndGreetAvailable={meetAndGreetAvailable}
                toggleOptionalAmenity={this.props.toggleOptionalAmenity}
                meetAndGreetChosen={upgrades[UPGRADE_IDS.MEET_AND_GREET]}
                onClickOk={this.handleShowSuggestedTripsDialog}
              />
            )}
          {!onDemand && (
            <Collapsible opened={showBookingForSomeoneElse}>
              {showBookingForSomeoneElse && (
                <TravelerData
                  onClickClose={this.handleTravelerDataClose}
                  formName={formName}
                  extraPaxRequired={extraPaxRequired}
                  passengersCount={passengersCount}
                />
              )}
            </Collapsible>
          )}

          {config.LOYALTY_PROGRAM_ENABLED && (
            <div className={style.field}>
              <TranslatedField
                name={LOYALTY_MEMBER_NUMBER_FIELD_NAME}
                label={
                  <FormattedMessage
                    {...messages.loyalty}
                    values={{ company: config.LOYALTY_PROGRAM_NAME }}
                  />
                }
                normalize={normalizeLoyaltyNumber}
                component={TextField}
                appearance="btp-field"
              />
            </div>
          )}

          <Flex size="noshrink" className={style.field}>
            <SpecialInstructions placeholder={specialInstructionsPlaceholder} />
          </Flex>

          {(isTravelAgent || isTACredits) &&
            config.AGENT_IATA_AND_ID_ENABLED && (
              <div className={style.field}>
                <Row wrap="xs">
                  <Field
                    name={TA_IATA_FIELD_NAME}
                    label={<FormattedMessage {...messages.iataCode} />}
                    component={TextField}
                    appearance="btp-field"
                  />
                  <Field
                    name={TA_AGENT_ID_FIELD_NAME}
                    label={
                      <FormattedMessage
                        {...messages.agentId}
                        values={{ company: config.TRAVEL_AGENT_COMPANY_NAME }}
                      />
                    }
                    component={TextField}
                    appearance="btp-field"
                  />
                </Row>
              </div>
            )}

          {loggedIn &&
            (isTravelAgent || isTACredits) &&
            !config.TUI_CS_ADJUSTMENTS && (
              <Flex size="noshrink" className={style.field}>
                <Field
                  compact
                  component={TransitionInput}
                  name={TA_CONFIRMATION_EMAIL_FIELD_NAME}
                  type="email"
                  label={
                    <FormattedMessage {...messages.travelAgentConfirmation} />
                  }
                  placeholder={
                    <FormattedMessage {...messages.travelAgentConfirmation} />
                  }
                  upwards
                />
              </Flex>
            )}

          {!loggedIn && !emailTaken && config.SHOW_SIGNIN_BUTTON && (
            <Flex size="noshrink" className={style.field}>
              <PasswordBox mobile={mobile} />
            </Flex>
          )}

          {hook({
            name: EXTRA_CONTACT_FIELDS_HOOK,
            defaultHandler: () => null,
          })}

          <SmsNotificationsAmenity />
        </Layout>
      );
    }
  }

  // TODO: move to Flow types
  // ContactInformation.propTypes = {
  //   mobile: PropTypes.bool,
  //   upgrades: PropTypes.array,
  //   emailTaken: PropTypes.bool,
  //   loggedIn: PropTypes.bool,
  //   isTravelAgent: PropTypes.bool,
  //   isTACredits: PropTypes.bool,
  //   isFromAirport: PropTypes.bool,
  //   phonePrefix: PropTypes.string,
  //   noPhone: PropTypes.bool,
  //   travelerOpened: PropTypes.bool,
  //   saveRedirectBackUrl: PropTypes.func.isRequired,
  //   customerProfiles: PropTypes.array,
  //   changeFieldValue: PropTypes.func.isRequired,
  //   meetAndGreetAvailable: PropTypes.bool,
  //   meetAndGreetDefault: PropTypes.bool,
  //   meetAndGreetChosen: PropTypes.bool,
  //   meetAndGreetBoxClosed: PropTypes.bool,
  //   passengersCount: PropTypes.number.isRequired,
  //   extraPaxRequired: PropTypes.bool,
  //   specialInstructionsPlaceholder: PropTypes.string,
  //   amenities: PropTypes.object,
  //   toggleOptionalAmenity: PropTypes.func,
  //   getSuggestedMeetAndGreetTrips: PropTypes.func,
  //   updateMeetAndGreetTrip: PropTypes.func,
  //   formName: PropTypes.string.isRequired,
  //   // hocs
  //   subpages: PropTypes.object.isRequired,
  //   history: PropTypes.object.isRequired,
  //   location: PropTypes.object.isRequired
  // };

  return compose(
    connectSubpages(),
    connect(mapStateToProps, mapDispatchToProps, mergeProps),
    withRouter
  )(ContactInformation);
};

export default builder;
